import { Controller } from "@hotwired/stimulus"
import SlimSelect from "slim-select"

// Todo <select> do admin vira campo com busca, sem precisar tocar em cada tela.
// Numa lista de 27 estados ou 30 colaboradores isso troca
// caça-ao-item-pela-inicial por digitar três letras; nas curtas, mantém o
// comportamento igual em toda parte — quem usa não precisa descobrir, a cada
// campo, se ali dá para digitar. Quem quiser ficar de fora usa data-no-search.

export default class extends Controller {
  connect() {
    this.instances = new Map()
    this.scan()

    // Formulários que nascem depois (linhas de contato, beneficiários) e
    // selects que outro controller repopula (estado → cidade) precisam ser
    // pegos também — senão a busca só existe no que veio no primeiro HTML.
    this.observer = new MutationObserver((records) => this.onMutations(records))
    this.observer.observe(this.element, { childList: true, subtree: true })

    // O slim-select esconde o <select> original com display:none. Um select
    // required e vazio nesse estado faz o navegador recusar o submit e NÃO
    // mostrar aviso nenhum — ele só escreve "is not focusable" no console. Pra
    // quem está na tela, o botão simplesmente não faz nada. `invalid` não
    // borbulha, por isso a captura.
    this.onInvalid = (event) => this.markInvalid(event)
    this.element.addEventListener("invalid", this.onInvalid, true)
  }

  disconnect() {
    this.element.removeEventListener("invalid", this.onInvalid, true)
    this.observer?.disconnect()
    // Destruir devolve o <select> original ao DOM: sem isso o cache do Turbo
    // guardaria a página já enfeitada e ela voltaria com dois campos.
    Array.from(this.instances.keys()).forEach((select) => this.release(select))
  }

  // Um <select> pode sair do DOM sem passar por aqui — troca de turbo-frame,
  // por exemplo. Nesse caso o destroy do slim-select estoura tentando devolver
  // o original a um pai que não existe mais; o campo já foi, e o que importa é
  // esquecer a instância.
  release(select) {
    const instance = this.instances.get(select)
    this.instances.delete(select)
    try {
      instance?.destroy()
    } catch {
      // nada a desfazer: o campo já saiu da página
    }
  }

  onMutations(records) {
    const touched = records
      .map((record) => record.target)
      .filter((target) => this.instances.has(target))

    // Opções trocadas por outro controller: reconstrói, senão a busca continua
    // oferecendo a lista velha.
    touched.forEach((select) => this.release(select))

    // Limpa o que saiu junto com um frame trocado.
    Array.from(this.instances.keys()).forEach((select) => {
      if (!select.isConnected) this.release(select)
    })

    clearTimeout(this.scanTimer)
    this.scanTimer = setTimeout(() => this.scan(), 50)
  }

  scan() {
    this.element.querySelectorAll("select").forEach((select) => this.enhance(select))
  }

  enhance(select) {
    if (!this.eligible(select)) return

    const blank = Array.from(select.options).find((option) => option.value === "")
    const instance = new SlimSelect({
      select,
      settings: {
        showSearch: true,
        searchPlaceholder: "Buscar...",
        searchText: "Nada encontrado",
        placeholderText: blank?.text || "Selecione",
        allowDeselect: !select.required,
        closeOnSelect: true
      }
    })

    this.keepExternalSelection(instance, select)
    this.instances.set(select, instance)
  }

  // Regressão de "select longo com busca em todo o admin": a cada `change` o
  // slim-select reescreve o <select> a partir da loja dele casando pelo ID do
  // <option>. As nossas options não têm id — não casava nada e ele DESMARCAVA
  // tudo. Era isso que apagava o estado preenchido pelo CEP.
  keepExternalSelection(instance, select) {
    instance.select.onValueChange = (options) => {
      const conhecidos = new Set()
      instance.getData().forEach((item) => {
        (item.options || [item]).forEach((opcao) => {
          conhecidos.add(opcao.value)
          if (opcao.id) conhecidos.add(opcao.id)
        })
      })

      // Lista trocada por fora (estado → cidade): a loja ainda é a antiga, e
      // reescrever agora apagaria as opções recém-criadas. É preciso olhar a
      // LISTA e não o valor — a troca começa inserindo a opção vazia, que a
      // loja conhece e passaria batido. Saindo daqui, o onOptionsChange do
      // próprio slim-select relê o DOM e acerta lista e seleção de uma vez.
      const listaEmDia = Array.from(select.options).every((o) => conhecidos.has(o.value))
      if (!listaEmDia) return

      instance.setSelected(options.map((opcao) => opcao.id || opcao.value))
    }
  }

  markInvalid(event) {
    const select = event.target
    if (!this.instances.has(select)) return

    const campo = select.nextElementSibling
    if (!campo?.classList.contains("ss-main")) return

    // Estilo em linha, não classe: o Tailwind não varre este arquivo e a classe
    // não existiria no CSS compilado.
    campo.style.outline = "2px solid #dc2626"
    campo.style.outlineOffset = "1px"
    campo.style.borderRadius = "0.5rem"
    select.addEventListener("change", () => this.clearInvalid(campo), { once: true })

    // Se algum campo VISÍVEL também estiver inválido, o navegador já leva o
    // vendedor até ele — não disputar o foco. Só quando todos os inválidos
    // estão escondidos é que ninguém avisaria nada.
    clearTimeout(this.invalidTimer)
    this.invalidTimer = setTimeout(() => this.scrollToFirstHidden(select.form), 0)
  }

  clearInvalid(campo) {
    campo.style.outline = ""
    campo.style.outlineOffset = ""
    campo.style.borderRadius = ""
  }

  scrollToFirstHidden(form) {
    if (!form) return

    // validity.valid é leitura pura; checkValidity() dispararia `invalid` de
    // novo e entraria em laço.
    const invalidos = Array.from(form.elements)
      .filter((el) => el.willValidate && !el.validity.valid)
    if (invalidos.length === 0) return
    if (invalidos.some((el) => !this.instances.has(el))) return

    invalidos[0].nextElementSibling?.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  eligible(select) {
    if (this.instances.has(select)) return false
    if (select.multiple || select.disabled) return false
    if ("noSearch" in select.dataset) return false
    // Já tratado à mão na própria tela (multi-seleção de produtos, times).
    if (select.closest("[data-controller~='slim-select']")) return false
    // O SlimSelect esconde o original e marca aria-hidden — é como se reconhece
    // um select que já foi enfeitado (por aqui ou pela tela, na mão).
    if (select.getAttribute("aria-hidden") === "true") return false
    // Sem opção nenhuma não há o que enfeitar (select que outro controller
    // preenche depois — a observação do DOM pega quando ele encher).
    return select.options.length > 0
  }
}
